// Importation des hooks React pour la gestion de l'état et des effets secondaires
import { useState, useEffect } from 'react';

// Importation des composants Recharts pour créer des graphiques (barres et circulaire)
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, PieChart, Pie, Legend } from 'recharts';

// Importation des icônes de Lucide pour les éléments visuels de l'interface
import { Download, Grid, Users, FileText, Bell, AlertTriangle, UserX, LockIcon, ChevronDown, Settings, TrendingUp, ChartBar, Zap, Shield, Activity } from 'lucide-react';

// Importation des composants de navigation personnalisés pour le tableau de bord
import Navbar0 from '../../../vitrine/Components/Navbar';
import { Navbar } from '../../pages/Dashboard/DashboarEntreprise/Navbar';

// Importation de file-saver pour exporter les données au format CSV
import { saveAs } from 'file-saver';

// Importation de useSWR pour la récupération de données avec mise en cache et revalidation
import useSWR from 'swr';

// Importation d'Axios pour effectuer des requêtes HTTP
import axios from 'axios';

// Importation de useNavigate pour la navigation programmatique avec React Router
import { useNavigate } from 'react-router-dom';

// Importation du contexte de traduction pour l'internationalisation
import { useTranslation } from '../../../TranslationContext';

// Importation d'un composant modal pour gérer les notifications
import NotificationListModal from './EditNotificationlist';

// Interfaces TypeScript pour assurer la sécurité des types
// Définit la structure d'une option de période pour le menu déroulant
interface PeriodOption {
  label: string;
  value: number;
}

// Définit les propriétés (props) du composant StatSummaryCard
interface StatSummaryCardProps {
  title: string; // Titre de la carte
  value: number; // Valeur affichée (pourcentage)
  icon: React.ElementType; // Icône à afficher
  color: string; // Couleur associée à la carte
  trend: number; // Tendance (variation par rapport à la période précédente)
  onClick: () => void; // Fonction appelée lors du clic sur la carte
}

// Définit la structure des statistiques d'une application
interface AppStats {
  name: string; // Nom de l'application
  authPercentage: number; // Pourcentage d'authentifications réussies
  failedPercentage: number; // Pourcentage d'échecs de connexion
  lockedPercentage: number; // Pourcentage de comptes verrouillés
  inactivePercentage: number; // Pourcentage d'utilisateurs inactifs
}

// Définit les propriétés du composant AnalyticsHub
interface AnalyticsHubProps {
  appStats: AppStats[]; // Liste des statistiques des applications
  isLoading: boolean; // Indicateur de chargement
  navigate: ReturnType<typeof useNavigate>; // Fonction de navigation
}

// Palette de couleurs pour un style cohérent dans le tableau de bord
const COLORS = {
  primary: '#c06a70', // Couleur principale pour les boutons, surlignages, etc.
  secondary: '#879bae', // Couleur secondaire pour les éléments moins proéminents
  background: '#f5f5dc', // Couleur de fond générale
  darkText: '#333333', // Couleur du texte sombre
  lightText: '#ffffff', // Couleur du texte clair
  surface: '#ffffff', // Couleur des surfaces (ex. cartes)
  border: '#e2e8f0', // Couleur des bordures
  hover: '#d6575d', // Couleur au survol
  dark: '#333333', // Couleur sombre
  light: 'Lightbulb', // Non utilisé dans ce code (peut-être une erreur ou un placeholder)
};

// Couleurs spécifiques pour les graphiques
const CHART_COLORS = {
  auth: '#c06a70', // Couleur pour les authentifications
  failed: '#879bae', // Couleur pour les échecs de connexion
  locked: '#a58e8f', // Couleur pour les comptes verrouillés
  inactive: '#333333', // Couleur pour les utilisateurs inactifs
};

const API_BASE_URL = import.meta.env.VITE_API;
// Créer une instance axios avec les headers communs
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials:true

});
apiClient.interceptors.response.use(
  response => response,
  async error => {
    if(error.response?.status === 401 && error.response.headers['x-token-expired'] === 'true')
    {
        await new Promise(resolve => setTimeout(resolve, 100))
      console.log("retry request....")
        return axios(error.configuration)
    }

    return Promise.reject(error);
  })






  
// Fonction de récupération des données avec Axios pour SWR
const axiosFetcher = async (url: string) => {
  // Envoie une requête GET avec les informations d'authentification
  const response = await apiClient.get(url, {
    withCredentials: true, // Inclut les cookies pour l'authentification
    // headers: {
    //   Authorization: `Bearer ${localStorage.getItem('access_token')}`, // Commenté, peut-être pour une future implémentation
    // },
  });
  return response.data; // Retourne les données de la réponse
};

// Interface pour les propriétés du composant CompanyStatsChart
interface CompanyStatsChartProps {
  title: string; // Titre du graphique
  icon: React.ElementType; // Icône associée
  data: { name: string; value: number; fill: string }[]; // Données pour le graphique
  periodLabel: string; // Label de la période affichée
  warning?: string; // Message d'avertissement optionnel
  additionalInfo?: string; // Information supplémentaire optionnelle
  isLoading?: boolean; // Indicateur de chargement
}


// Composant principal de la page d'accueil
export default function LandingPage() {
  const navigate = useNavigate(); // Hook pour la navigation
  const { t } = useTranslation(); // Hook pour la traduction
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>({ label: t('3_months'), value: 3 }); // Période sélectionnée
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false); // État du menu déroulant de période
  const [isMenuOpen, setIsMenuOpen] = useState(false); // État du menu principal
  const [isLoading, setIsLoading] = useState(true); // État de chargement
  const companyId = localStorage.getItem('company'); // Identifiant de l'entreprise depuis le stockage local
  const API_BASE_URL = import.meta.env.VITE_API; // URL de base de l'API depuis les variables d'environnement
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false); // État du modal de notifications

  // Options de période pour le menu déroulant
  const PERIOD_OPTIONS: PeriodOption[] = [
    { label: t('months'), value: 3 },
    { label: t('monthss'), value: 6 },
    { label: t('year'), value: 12 },
    { label: t('all_time'), value: 0 },
  ];

  // Récupération des données des applications avec SWR, avec configuration pour limiter les revalidations
const { data: applicationsData, error: applicationsError } = useSWR(
  companyId ? `${API_BASE_URL}/Company/${companyId}/applications` : null,
  axiosFetcher,
  {
    revalidateOnFocus: false, // Désactive la revalidation au focus de la fenêtre
    revalidateOnReconnect: false, // Désactive la revalidation à la reconnexion réseau
    refreshInterval: 0, // Désactive le rafraîchissement automatique
    dedupingInterval: 60000, // Évite les appels répétés dans un intervalle de 60 secondes
  }
);

// État pour stocker les statistiques des applications
const [appStats, setAppStats] = useState<AppStats[]>([]);

// Effet pour récupérer les données des utilisateurs pour chaque application
useEffect(() => {
  const fetchUsersData = async () => {
    if (!companyId || !applicationsData?.data) {
      setIsLoading(false); // Arrête le chargement si pas de données
      return;
    }

    setIsLoading(true);
    const stats: AppStats[] = [];

    for (const app of applicationsData.data) {
      try {
        const response = await apiClient.get(
          `/Company/${companyId}/applications/${app.id}/users`,
          
        );

        if (response.data && response.data.data) {
          const users = response.data.data;
          const totalUsers = users.length;

          if (totalUsers === 0) {
            stats.push({
              name: app.displayName,
              authPercentage: 0,
              failedPercentage: 0,
              lockedPercentage: 0,
              inactivePercentage: 0,
            });
            continue;
          }

          const now = new Date();
          const periodStart = new Date();
          if (selectedPeriod.value > 0) {
            periodStart.setMonth(now.getMonth() - selectedPeriod.value);
          }

          const authenticatedUsers = users.filter((user: { lastSuccessful: string | number | Date }) => {
            if (!user.lastSuccessful) return false;
            const lastAuthDate = new Date(user.lastSuccessful);
            return selectedPeriod.value === 0 || lastAuthDate >= periodStart;
          }).length;

          const failedLogins = users.filter((user: { failureResponse: string | number | Date }) => {
            if (!user.failureResponse || user.failureResponse === '1960-06-01T00:00:00') return false;
            const failureDate = new Date(user.failureResponse);
            return selectedPeriod.value === 0 || failureDate >= periodStart;
          }).length;

          const lockedAccounts = users.filter((user: { status: string }) => user.status === 'Locked').length;

          const inactiveUsers = users.filter((user: { lastActivity: string | number | Date; lastSuccessful: string | number | Date }) => {
            const lastActivityDate = user.lastActivity
              ? new Date(user.lastActivity)
              : user.lastSuccessful
              ? new Date(user.lastSuccessful)
              : null;
            return !lastActivityDate || (selectedPeriod.value > 0 && lastActivityDate < periodStart);
          }).length;

          stats.push({
            name: app.displayName,
            authPercentage: Math.round((authenticatedUsers / totalUsers) * 100),
            failedPercentage: Math.round((failedLogins / totalUsers) * 100),
            lockedPercentage: Math.round((lockedAccounts / totalUsers) * 100),
            inactivePercentage: Math.round((inactiveUsers / totalUsers) * 100),
          });
        }
      } catch (error) {
        console.error(`Erreur lors de la récupération des utilisateurs pour l'application ${app.id}:`, error);
      }
    }

    setAppStats(stats);
    setIsLoading(false);
  };

  fetchUsersData();
}, [applicationsData?.data, companyId, selectedPeriod.value, API_BASE_URL]); // Dépendances stabilisées

  // Préparation des données pour les graphiques
  const getAuthChartData = () => {
    return appStats.map((app) => ({
      name: app.name,
      value: app.authPercentage,
      fill: CHART_COLORS.auth,
    }));
  };

  const getFailedChartData = () => {
    return appStats.map((app) => ({
      name: app.name,
      value: app.failedPercentage,
      fill: CHART_COLORS.failed,
    }));
  };

  const getLockedChartData = () => {
    return appStats.map((app) => ({
      name: app.name,
      value: app.lockedPercentage,
      fill: CHART_COLORS.locked,
    }));
  };

  const getInactiveChartData = () => {
    return appStats.map((app) => ({
      name: app.name,
      value: app.inactivePercentage,
      fill: CHART_COLORS.inactive,
    }));
  };

  // Fonction pour exporter les données en CSV
  const exportCSV = () => {
    const headers = `${t('application')},${t('authentication_rate')},${t('failed_logins')},${t('locked_accounts')},${t('inactive_users')}\n`;
    const rows = appStats
      .map((app) => `"${app.name}",${app.authPercentage}%,${app.failedPercentage}%,${app.lockedPercentage}%,${app.inactivePercentage}%`)
      .join('\n');

    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${t('company_stats_report')}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Gestion des erreurs et états de chargement
  const hasError = applicationsError;
  const isInitialLoading = !applicationsData && !applicationsError;

  return (
    // Conteneur principal de la page
    <div className="min-h-screen" style={{ backgroundColor: COLORS.light }}>
      <div>
        <Navbar0 /> {/* Barre de navigation principale */}
        <Navbar /> {/* Barre de navigation secondaire */}
      </div>

      {/* Contenu principal */}
      <div className="pt-16">
        {/* Sélecteur de période et menu */}
        <div className="bg-white border-b shadow-sm" style={{ borderColor: COLORS.border }}>
          <div className="max-w-7xl mx-auto px-8 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div className="relative mb-4 sm:mb-0">
              <div className="flex items-center">
                <span className="mr-2 font-medium" style={{ color: COLORS.dark }}>{t('period')}</span>
                <button
                  onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border rounded-lg hover:bg-slate-50 transition-colors focus:outline-none"
                  style={{ borderColor: COLORS.border, color: COLORS.dark }}
                  aria-label={t('select_period')}
                >
                  <span>{selectedPeriod.label}</span>
                  <ChevronDown size={16} style={{ color: COLORS.secondary }} />
                </button>
              </div>

              {isPeriodDropdownOpen && (
                // Menu déroulant pour sélectionner la période
                <div className="absolute left-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-10" style={{ borderColor: COLORS.border }}>
                  {PERIOD_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedPeriod(option);
                        setIsPeriodDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors first:rounded-t-lg last:rounded-b-lg border-b last:border-0"
                      style={{ color: COLORS.dark, borderColor: COLORS.border }}
                      aria-label={t('select_option', { label: option.label })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Bouton de menu avec options */}
            <div className="relative">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors focus:outline-none"
                style={{ backgroundColor: COLORS.primary }}
                aria-label={t('menu')}
              >
                <Grid size={18} />
                <span className="font-medium">{t('menu')}</span>
              </button>

              {isMenuOpen && (
                // Menu déroulant des options
                <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10" style={{ borderColor: COLORS.border }}>
                  <button
                    onClick={() => navigate('/EntrepriseDashboard')}
                    className="block w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b items-center"
                    style={{ color: COLORS.dark, borderColor: COLORS.border }}
                    aria-label={t('dashboard_company')}
                  >
                    <Grid size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('dashboard_company')}
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false); // Ferme le menu
                      setIsNotificationModalOpen(true); // Ouvre le modal de notifications
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b flex items-center"
                    style={{ color: COLORS.dark, borderColor: COLORS.border }}
                    aria-label={t('edit_notification_list')}
                  >
                    <Bell size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('edit_notification_list')}
                  </button>
                  <button
                    onClick={() => navigate('/BecomeClient')}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b flex items-center"
                    style={{ color: COLORS.dark, borderColor: COLORS.border }}
                    aria-label={t('request_registration')}
                  >
                    <FileText size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('request_registration')}
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center"
                    style={{ color: COLORS.dark }}
                    aria-label={t('settings')}
                  >
                    <Settings size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('settings')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Zone de contenu principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
          {hasError ? (
            // Affichage d'une erreur si la récupération des données échoue
            <div className="p-4 rounded-md shadow-sm mb-6" style={{ backgroundColor: '#f8e6e7', borderLeft: `4px solid ${COLORS.primary}` }}>
              <div className="flex">
                <AlertTriangle className="h-6 w-6 mr-3" style={{ color: COLORS.primary }} />
                <div>
                  <p className="text-sm" style={{ color: COLORS.dark }}>{t('failed_to_load_data')}</p>
                </div>
              </div>
            </div>
          ) : isInitialLoading ? (
            // Indicateur de chargement initial
            <div className="flex justify-center items-center h-64">
              <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: '#f0e6e7', borderTopColor: COLORS.primary }}></div>
            </div>
          ) : (
            <>
              {/* Grille des graphiques */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <CompanyStatsChart
                  title={t('authentication_rate_by_app')}
                  icon={Users}
                  data={getAuthChartData()}
                  periodLabel={selectedPeriod.label}
                  isLoading={isLoading}
                  warning={appStats.some((app) => app.authPercentage < 50) ? t('low_authentication_warning') : undefined}
                />
                <CompanyStatsChart
                  title={t('login_failures_by_app')}
                  icon={AlertTriangle}
                  data={getFailedChartData()}
                  periodLabel={selectedPeriod.label}
                  isLoading={isLoading}
                  warning={appStats.some((app) => app.failedPercentage > 20) ? t('high_failure_warning') : undefined}
                />
                <CompanyStatsChart
                  title={t('locked_accounts_by_app')}
                  icon={LockIcon}
                  data={getLockedChartData()}
                  periodLabel={selectedPeriod.label}
                  isLoading={isLoading}
                  warning={appStats.some((app) => app.lockedPercentage > 10) ? t('high_lock_warning') : undefined}
                />
                <CompanyStatsChart
                  title={t('user_inactivity_by_app')}
                  icon={UserX}
                  data={getInactiveChartData()}
                  periodLabel={selectedPeriod.label}
                  isLoading={isLoading}
                  warning={appStats.some((app) => app.inactivePercentage > 40) ? t('high_inactivity_warning') : undefined}
                />
              </div>

              {/* Section d'exportation */}
              <div className="bg-white p-6 rounded-xl shadow-md border mb-8" style={{ borderColor: COLORS.border }}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div>
                    <h3 className="text-xl font-semibold" style={{ color: COLORS.dark }}>{t('global_export')}</h3>
                    <p className="mt-1" style={{ color: COLORS.secondary }}>{t('global_export_description')}</p>
                  </div>
                  <button
                    onClick={exportCSV}
                    className="flex items-center px-5 py-2.5 text-white rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: COLORS.primary }}
                    disabled={isLoading || appStats.length === 0}
                    aria-label={t('export_full_report')}
                  >
                    <Download size={18} className="mr-2" />
                    {t('export_full_report')}
                  </button>
                </div>

                {isLoading ? (
                  <div className="mt-4 flex items-center" style={{ color: COLORS.secondary }}>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin mr-2" style={{ borderColor: '#e9ecf0', borderTopColor: COLORS.primary }}></div>
                    <span>{t('preparing_data')}</span>
                  </div>
                ) : appStats.length === 0 ? (
                  <div className="mt-2" style={{ color: COLORS.secondary }}>{t('no_data_for_export')}</div>
                ) : null}
              </div>

              {/* Centre d'analyse */}
              <AnalyticsHub appStats={appStats} isLoading={isLoading} navigate={navigate} />
            </>
          )}
        </div>
        {/* Modal pour gérer la liste des notifications */}
        <NotificationListModal 
          isOpen={isNotificationModalOpen} 
          onClose={() => setIsNotificationModalOpen(false)} 
        />
      </div>
    </div>
  );
}



// Composant pour afficher un graphique de statistiques d'entreprise
const CompanyStatsChart = ({
  title,
  icon: Icon,
  data,
  periodLabel,
  warning,
  additionalInfo,
  isLoading = false,
}: CompanyStatsChartProps) => {
  const { t } = useTranslation(); // Hook pour la traduction

  return (
    // Conteneur du graphique avec ombre et bordure
    <div className="bg-white shadow-md rounded-xl p-6 h-full border border-slate-200 transition-all duration-300 hover:shadow-lg">
      {/* En-tête avec icône et titre */}
      <div className="flex items-center mb-5">
        <div className="p-2.5 bg-opacity-10 rounded-lg mr-3 text-primary" style={{ backgroundColor: `${COLORS.primary}20` }}>
          <Icon className="h-6 w-6" style={{ color: COLORS.primary }} />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: COLORS.darkText }}>{title}</h2>
      </div>

      {/* Zone du graphique */}
      <div className="h-64">
        {isLoading ? (
          // Indicateur de chargement
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: `${COLORS.secondary}`, borderTopColor: COLORS.primary }}></div>
          </div>
        ) : data.length === 0 ? (
          // Message si aucune donnée n'est disponible
          <div className="h-full flex items-center justify-center text-center">
            <p style={{ color: COLORS.secondary }}>{t('no_data_available')}</p>
          </div>
        ) : (
          // Graphique à barres responsive
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.secondary}30`} vertical={false} /> {/* Grille de fond */}
              <XAxis
                dataKey="name"
                tick={{ fill: COLORS.darkText, fontSize: 11 }}
                axisLine={{ stroke: COLORS.secondary }}
                tickLine={{ stroke: COLORS.secondary }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
              /> {/* Axe X avec noms des applications */}
              <YAxis
                domain={[0, 100]}
                tick={{ fill: COLORS.darkText, fontSize: 11 }}
                axisLine={{ stroke: COLORS.secondary }}
                tickLine={{ stroke: COLORS.secondary }}
                label={{ value: t('percentage'), angle: -90, position: 'insideLeft', fill: COLORS.secondary, fontSize: 12 }}
              /> {/* Axe Y pour les pourcentages */}
              <Tooltip
                formatter={(value) => [`${value}%`, t('percentage')]}
                contentStyle={{ backgroundColor: COLORS.surface, borderColor: COLORS.border, borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                itemStyle={{ color: COLORS.darkText }}
                labelStyle={{ color: COLORS.darkText, fontWeight: 'bold', marginBottom: '5px' }}
              /> {/* Infobulle pour les détails */}
              <Bar dataKey="value" barSize={24} radius={[4, 4, 0, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} /> // Barres colorées
                ))}
                <LabelList
                  dataKey="value"
                  position="top"
                  formatter={(value: any) => `${value}%`}
                  fill={COLORS.darkText}
                  fontSize={10}
                  fontWeight={500}
                /> {/* Étiquettes des valeurs au-dessus des barres */}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Informations supplémentaires */}
      <div className="mt-5 text-sm">
        <p style={{ color: COLORS.secondary }}>{t('statistics_over', { period: periodLabel })}</p>
        {warning && (
          // Avertissement si nécessaire
          <div className="mt-2 flex items-start p-2 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10`, color: COLORS.primary }}>
            <AlertTriangle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
            <p>{warning}</p>
          </div>
        )}
        {additionalInfo && <p className="mt-2" style={{ color: COLORS.secondary }}>{additionalInfo}</p>}
      </div>
    </div>
  );
};

// Composant pour afficher une carte récapitulative des statistiques
const StatSummaryCard = ({ title, value, icon: Icon, color, trend, onClick }: StatSummaryCardProps) => {
  const { t } = useTranslation();

  return (
    // Carte cliquable avec effet de survol
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:translate-y-1 transform"
      role="button"
      aria-label={t('view_details', { title })}
    >
      <div className="h-2" style={{ backgroundColor: color }}></div> {/* Bandeau coloré en haut */}
      <div className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm font-medium" style={{ color: COLORS.secondary }}>{title}</h3>
            <p className="text-3xl font-bold mt-1" style={{ color: COLORS.darkText }}>{value}%</p>
            <p className="text-xs mt-2 flex items-center" style={{ color: trend > 0 ? '#4caf50' : '#f44336' }}>
              {trend > 0 ? <TrendingUp size={14} className="mr-1" /> : <TrendingUp size={14} className="mr-1 transform rotate-180" />}
              {Math.abs(trend)}% {t('from_previous_period')}
            </p> {/* Tendance avec icône */}
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20`, color: color }}>
            <Icon size={24} /> {/* Icône associée */}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant principal pour le centre d'analyse
const AnalyticsHub = ({ appStats, isLoading, navigate }: AnalyticsHubProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('overview'); // État pour l'onglet actif

  // Calcule les moyennes des statistiques
  const getAverages = () => {
    if (appStats.length === 0) return { auth: 0, failed: 0, locked: 0, inactive: 0 };

    return {
      auth: Math.round(appStats.reduce((sum: any, app: { authPercentage: any }) => sum + app.authPercentage, 0) / appStats.length),
      failed: Math.round(appStats.reduce((sum: any, app: { failedPercentage: any }) => sum + app.failedPercentage, 0) / appStats.length),
      locked: Math.round(appStats.reduce((sum: any, app: { lockedPercentage: any }) => sum + app.lockedPercentage, 0) / appStats.length),
      inactive: Math.round(appStats.reduce((sum: any, app: { inactivePercentage: any }) => sum + app.inactivePercentage, 0) / appStats.length),
    };
  };

  const averages = getAverages();

  // Données pour le graphique circulaire
  const securityStatusData = [
    { name: t('authenticated'), value: averages.auth, fill: CHART_COLORS.auth },
    { name: t('failed_logins'), value: averages.failed, fill: CHART_COLORS.failed },
    { name: t('locked'), value: averages.locked, fill: CHART_COLORS.locked },
    { name: t('inactive'), value: averages.inactive, fill: CHART_COLORS.inactive },
  ];

  return (
    // Conteneur principal avec fond et ombre
    <div className="border border-slate-200 shadow-lg overflow-hidden rounded-xl" style={{ backgroundColor: COLORS.background }}>
      {/* En-tête du centre d'analyse */}
      <div className="p-6 text-white" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.primary}cc)` }}>
        <h2 className="text-2xl font-bold mb-2">{t('analytics_hub')}</h2>
        <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('analytics_hub_description')}</p>
      </div>

      {/* Onglets de navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setSelectedTab('overview')}
            className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
            style={{
              borderColor: selectedTab === 'overview' ? COLORS.primary : 'transparent',
              color: selectedTab === 'overview' ? COLORS.primary : COLORS.secondary,
            }}
            aria-label={t('overview')}
          >
            <ChartBar size={16} className="inline mr-2" />
            {t('overview')}
          </button>
          <button
            onClick={() => setSelectedTab('security')}
            className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
            style={{
              borderColor: selectedTab === 'security' ? COLORS.primary : 'transparent',
              color: selectedTab === 'security' ? COLORS.primary : COLORS.secondary,
            }}
            aria-label={t('security_status')}
          >
            <Shield size={16} className="inline mr-2" />
            {t('security_status')}
          </button>
          <button
            onClick={() => setSelectedTab('activity')}
            className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
            style={{
              borderColor: selectedTab === 'activity' ? COLORS.primary : 'transparent',
              color: selectedTab === 'activity' ? COLORS.primary : COLORS.secondary,
            }}
            aria-label={t('user_activity')}
          >
            <Activity size={16} className="inline mr-2" />
            {t('user_activity')}
          </button>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="p-6">
        {isLoading ? (
          // Indicateur de chargement
          <div className="h-64 flex items-center justify-center">
            <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: `${COLORS.secondary}50`, borderTopColor: COLORS.primary }}></div>
          </div>
        ) : (
          <>
            {selectedTab === 'overview' && (
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.darkText }}>{t('apps_performance_summary')}</h3>

                {/* Grille des cartes récapitulatives */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatSummaryCard
                    title={t('authentication_rate')}
                    value={averages.auth}
                    icon={Users}
                    color={COLORS.primary}
                    trend={5.2}
                    onClick={() => navigate('/users-management')}
                  />
                  <StatSummaryCard
                    title={t('failed_logins')}
                    value={averages.failed}
                    icon={AlertTriangle}
                    color={COLORS.secondary}
                    trend={-3.1}
                    onClick={() => navigate('/security-settings')}
                  />
                  <StatSummaryCard
                    title={t('locked_accounts')}
                    value={averages.locked}
                    icon={LockIcon}
                    color={CHART_COLORS.locked}
                    trend={1.8}
                    onClick={() => navigate('/security-settings')}
                  />
                  <StatSummaryCard
                    title={t('inactive_users')}
                    value={averages.inactive}
                    icon={UserX}
                    color={COLORS.darkText}
                    trend={-2.4}
                    onClick={() => navigate('/users-management')}
                  />
                </div>

                {/* Section d'informations rapides */}
                <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30` }}>
                  <div className="flex items-start">
                    <Zap size={20} className="mr-3 mt-0.5 flex-shrink-0" style={{ color: COLORS.primary }} />
                    <div>
                      <h4 className="font-medium mb-1" style={{ color: COLORS.darkText }}>{t('quick_insights')}</h4>
                      <p className="text-sm" style={{ color: COLORS.darkText }}>
                        {averages.auth < 70
                          ? t('low_authentication_insight')
                          : averages.failed > 15
                          ? t('high_failure_insight')
                          : averages.locked > 10
                          ? t('high_lockout_insight')
                          : averages.inactive > 30
                          ? t('high_inactivity_insight')
                          : t('healthy_metrics_insight')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section des actions recommandées */}
                <div className="flex justify-between items-center mt-8">
                  <h3 className="text-lg font-semibold" style={{ color: COLORS.darkText }}>{t('recommended_actions')}</h3>
                  <a href="#" className="text-sm font-medium hover:underline" style={{ color: COLORS.primary }}>
                    {t('view_all_recommendations')}
                  </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <button
                    onClick={() => navigate('/users-management')}
                    className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
                    aria-label={t('manage_users')}
                  >
                    <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.primary}20`, color: COLORS.primary }}>
                      <Users size={16} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('manage_users')}</h4>
                      <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('manage_users_description')}</p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/security-settings')}
                    className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
                    aria-label={t('security_settings')}
                  >
                    <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.secondary}20`, color: COLORS.secondary }}>
                      <Shield size={16} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('security_settings')}</h4>
                      <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('security_settings_description')}</p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate('/notification-settings')}
                    className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
                    aria-label={t('notifications')}
                  >
                    <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.primary}10`, color: COLORS.primary }}>
                      <Bell size={16} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('notifications')}</h4>
                      <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('notifications_description')}</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {selectedTab === 'security' && (
              <div>
                <h3 className="text-lg font-semibold mb-6" style={{ color: COLORS.darkText }}>{t('security_status_overview')}</h3>

                {/* Grille pour le graphique circulaire et l'évaluation des risques */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium mb-4" style={{ color: COLORS.darkText }}>{t('auth_status_distribution')}</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={securityStatusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {securityStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} /> // Segments colorés
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value}%`]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium mb-2" style={{ color: COLORS.darkText }}>{t('security_risk_assessment')}</h4>

                    {/* Barres de progression pour les métriques de sécurité */}
                    <div className="space-y-4 mt-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('authentication_health')}</span>
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.auth}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${averages.auth}%`,
                              backgroundColor: averages.auth > 80 ? '#4caf50' : averages.auth > 60 ? CHART_COLORS.locked : COLORS.primary,
                            }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('login_failures')}</span>
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.failed}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${averages.failed}%`,
                              backgroundColor: averages.failed < 10 ? '#4caf50' : averages.failed < 20 ? CHART_COLORS.locked : COLORS.primary,
                            }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('account_lockouts')}</span>
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.locked}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${averages.locked}%`,
                              backgroundColor: averages.locked < 5 ? '#4caf50' : averages.locked < 15 ? CHART_COLORS.locked : COLORS.primary,
                            }}
                          ></div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('user_activity')}</span>
                          <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{100 - averages.inactive}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${100 - averages.inactive}%`,
                              backgroundColor: averages.inactive < 20 ? '#4caf50' : averages.inactive < 40 ? CHART_COLORS.locked : COLORS.primary,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate('/security-settings')}
                      className="mt-6 w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
                      style={{ backgroundColor: COLORS.primary }}
                      aria-label={t('review_security_settings')}
                    >
                      {t('review_security_settings')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedTab === 'activity' && (
              <div>
                <h3 className="text-lg font-semibold mb-6" style={{ color: COLORS.darkText }}>{t('user_activity_metrics')}</h3>

                <div className="grid grid-cols-1 gap-6">
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium mb-4" style={{ color: COLORS.darkText }}>{t('active_vs_inactive_users')}</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={appStats.map((app: { name: any; inactivePercentage: number }) => ({
                            name: app.name,
                            active: 100 - app.inactivePercentage,
                            inactive: app.inactivePercentage,
                          }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={`${COLORS.secondary}30`} />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            tick={{ fontSize: 11, fill: COLORS.darkText }}
                          />
                          <YAxis domain={[0, 100]} tick={{ fill: COLORS.darkText }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="active" stackId="a" fill={CHART_COLORS.auth} name={t('active_users')} />
                          <Bar dataKey="inactive" stackId="a" fill={CHART_COLORS.inactive} name={t('inactive_users')} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-medium mb-3" style={{ color: COLORS.darkText }}>{t('user_engagement_summary')}</h4>
                      <p className="text-sm mb-4" style={{ color: COLORS.secondary }}>
                        {t('user_engagement_description',
                         { active: Math.round(100 - averages.inactive),
                          inactive: averages.inactive,
                        })}
                      </p>
                      <button
                        onClick={() => navigate('/users-management')}
                        className="w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
                        style={{ backgroundColor: COLORS.primary }}
                        aria-label={t('manage_users')}
                      >
                        {t('manage_users')}
                      </button>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-medium mb-3" style={{ color: COLORS.darkText }}>{t('security_event_summary')}</h4>
                      <p className="text-sm mb-4" style={{ color: COLORS.secondary }}>
                        {t('security_event_description', {
                          failed: averages.failed,
                          locked: averages.locked,
                        })}
                      </p>
                      <button
                        onClick={() => navigate('/notification-settings')}
                        className="w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
                        style={{ backgroundColor: COLORS.primary }}
                        aria-label={t('configure_alerts')}
                      >
                        {t('configure_alerts')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};































//  import { useState, useEffect } from 'react';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, PieChart, Pie, Legend } from 'recharts';
// import { Download, Grid, Users, FileText, Bell, AlertTriangle, UserX, LockIcon, ChevronDown, Settings, TrendingUp, ChartBar, Zap, Shield, Activity } from 'lucide-react';
// import Navbar0 from '../../../vitrine/Components/Navbar';
// import { Navbar } from '../../pages/Dashboard/DashboarEntreprise/Navbar';
// import { saveAs } from 'file-saver';
// import useSWR from 'swr';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import { useTranslation } from '../../../TranslationContext';
// import NotificationListModal from './EditNotificationlist';

// // Types
// interface PeriodOption {
//   label: string;
//   value: number;
// }

// interface StatSummaryCardProps {
//     title: string;
//     value: number;
//     icon: React.ElementType;
//     color: string;
//     trend: number;
//     onClick: () => void;
//   }
  

    

// interface AppStats {
//   name: string;
//   authPercentage: number;
//   failedPercentage: number;
//   lockedPercentage: number;
//   inactivePercentage: number;
// }

// interface AnalyticsHubProps {
//     appStats: AppStats[]; 
//     isLoading: boolean;
//     navigate: ReturnType<typeof useNavigate>;
//   }
  
 
  

// const COLORS = {
//   primary: '#c06a70',
//   secondary: '#879bae',
//   background: '#f5f5dc',
//   darkText: '#333333',
//   lightText: '#ffffff',
//   surface: '#ffffff',
//   border: '#e2e8f0',
//   hover: '#d6575d',
//   dark: '#333333',
//   light: 'Lightbulb',
// };

// const CHART_COLORS = {
//   auth: '#c06a70',
//   failed: '#879bae',
//   locked: '#a58e8f',
//   inactive: '#333333',
// };

// // Fetcher for SWR
// const axiosFetcher = async (url: string) => {
//   const response = await axios.get(url, {
//     withCredentials:true,
//     // headers: {
//     //   Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//     // },
//   });
//   return response.data;
// };

// interface CompanyStatsChartProps {
//   title: string;
//   icon: React.ElementType;
//   data: { name: string; value: number; fill: string }[];
//   periodLabel: string;
//   warning?: string;
//   additionalInfo?: string;
//   isLoading?: boolean;
// }

// const CompanyStatsChart = ({
//   title,
//   icon: Icon,
//   data,
//   periodLabel,
//   warning,
//   additionalInfo,
//   isLoading = false,
// }: CompanyStatsChartProps) => {
//   const { t } = useTranslation();

//   return (
//     <div className="bg-white shadow-md rounded-xl p-6 h-full border border-slate-200 transition-all duration-300 hover:shadow-lg">
//       <div className="flex items-center mb-5">
//         <div className="p-2.5 bg-opacity-10 rounded-lg mr-3 text-primary" style={{ backgroundColor: `${COLORS.primary}20` }}>
//           <Icon className="h-6 w-6" style={{ color: COLORS.primary }} />
//         </div>
//         <h2 className="text-lg font-semibold" style={{ color: COLORS.darkText }}>{title}</h2>
//       </div>

//       <div className="h-64">
//         {isLoading ? (
//           <div className="h-full flex items-center justify-center">
//             <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: `${COLORS.secondary}`, borderTopColor: COLORS.primary }}></div>
//           </div>
//         ) : data.length === 0 ? (
//           <div className="h-full flex items-center justify-center text-center">
//             <p style={{ color: COLORS.secondary }}>{t('no_data_available')}</p>
//           </div>
//         ) : (
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
//               <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.secondary}30`} vertical={false} />
//               <XAxis
//                 dataKey="name"
//                 tick={{ fill: COLORS.darkText, fontSize: 11 }}
//                 axisLine={{ stroke: COLORS.secondary }}
//                 tickLine={{ stroke: COLORS.secondary }}
//                 interval={0}
//                 angle={-45}
//                 textAnchor="end"
//                 height={70}
//               />
//               <YAxis
//                 domain={[0, 100]}
//                 tick={{ fill: COLORS.darkText, fontSize: 11 }}
//                 axisLine={{ stroke: COLORS.secondary }}
//                 tickLine={{ stroke: COLORS.secondary }}
//                 label={{ value: t('percentage'), angle: -90, position: 'insideLeft', fill: COLORS.secondary, fontSize: 12 }}
//               />
//               <Tooltip
//                 formatter={(value) => [`${value}%`, t('percentage')]}
//                 contentStyle={{ backgroundColor: COLORS.surface, borderColor: COLORS.border, borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
//                 itemStyle={{ color: COLORS.darkText }}
//                 labelStyle={{ color: COLORS.darkText, fontWeight: 'bold', marginBottom: '5px' }}
//               />
//               <Bar dataKey="value" barSize={24} radius={[4, 4, 0, 0]}>
//                 {data.map((entry, index) => (
//                   <Cell key={`cell-${index}`} fill={entry.fill} />
//                 ))}
//                 <LabelList
//                   dataKey="value"
//                   position="top"
//                   formatter={(value: any) => `${value}%`}
//                   fill={COLORS.darkText}
//                   fontSize={10}
//                   fontWeight={500}
//                 />
//               </Bar>
//             </BarChart>
//           </ResponsiveContainer>
//         )}
//       </div>

//       <div className="mt-5 text-sm">
//         <p style={{ color: COLORS.secondary }}>{t('statistics_over', { period: periodLabel })}</p>
//         {warning && (
//           <div className="mt-2 flex items-start p-2 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10`, color: COLORS.primary }}>
//             <AlertTriangle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
//             <p>{warning}</p>
//           </div>
//         )}
//         {additionalInfo && <p className="mt-2" style={{ color: COLORS.secondary }}>{additionalInfo}</p>}
//       </div>
//     </div>
//   );
// };

// const StatSummaryCard = ({ title, value, icon: Icon, color, trend, onClick }: StatSummaryCardProps) => {
//   const { t } = useTranslation();

//   return (
//     <div
//       onClick={onClick}
//       className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:translate-y-1 transform"
//       role="button"
//       aria-label={t('view_details', { title })}
//     >
//       <div className="h-2" style={{ backgroundColor: color }}></div>
//       <div className="p-5">
//         <div className="flex justify-between items-start">
//           <div>
//             <h3 className="text-sm font-medium" style={{ color: COLORS.secondary }}>{title}</h3>
//             <p className="text-3xl font-bold mt-1" style={{ color: COLORS.darkText }}>{value}%</p>
//             <p className="text-xs mt-2 flex items-center" style={{ color: trend > 0 ? '#4caf50' : '#f44336' }}>
//               {trend > 0 ? <TrendingUp size={14} className="mr-1" /> : <TrendingUp size={14} className="mr-1 transform rotate-180" />}
//               {Math.abs(trend)}% {t('from_previous_period')}
//             </p>
//           </div>
//           <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20`, color: color }}>
//             <Icon size={24} />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const AnalyticsHub = ({ appStats, isLoading, navigate }: AnalyticsHubProps) => {
//   const { t } = useTranslation();
//   const [selectedTab, setSelectedTab] = useState('overview');

//   const getAverages = () => {
//     if (appStats.length === 0) return { auth: 0, failed: 0, locked: 0, inactive: 0 };

//     return {
//       auth: Math.round(appStats.reduce((sum: any, app: { authPercentage: any }) => sum + app.authPercentage, 0) / appStats.length),
//       failed: Math.round(appStats.reduce((sum: any, app: { failedPercentage: any }) => sum + app.failedPercentage, 0) / appStats.length),
//       locked: Math.round(appStats.reduce((sum: any, app: { lockedPercentage: any }) => sum + app.lockedPercentage, 0) / appStats.length),
//       inactive: Math.round(appStats.reduce((sum: any, app: { inactivePercentage: any }) => sum + app.inactivePercentage, 0) / appStats.length),
//     };
//   };

//   const averages = getAverages();

//   const securityStatusData = [
//     { name: t('authenticated'), value: averages.auth, fill: CHART_COLORS.auth },
//     { name: t('failed_logins'), value: averages.failed, fill: CHART_COLORS.failed },
//     { name: t('locked'), value: averages.locked, fill: CHART_COLORS.locked },
//     { name: t('inactive'), value: averages.inactive, fill: CHART_COLORS.inactive },
//   ];

//   return (
//     <div className="border border-slate-200 shadow-lg overflow-hidden rounded-xl" style={{ backgroundColor: COLORS.background }}>
//       <div className="p-6 text-white" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.primary}cc)` }}>
//         <h2 className="text-2xl font-bold mb-2">{t('analytics_hub')}</h2>
//         <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('analytics_hub_description')}</p>
//       </div>

//       <div className="bg-white border-b border-slate-200">
//         <div className="flex overflow-x-auto">
//           <button
//             onClick={() => setSelectedTab('overview')}
//             className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
//             style={{
//               borderColor: selectedTab === 'overview' ? COLORS.primary : 'transparent',
//               color: selectedTab === 'overview' ? COLORS.primary : COLORS.secondary,
//             }}
//             aria-label={t('overview')}
//           >
//             <ChartBar size={16} className="inline mr-2" />
//             {t('overview')}
//           </button>
//           <button
//             onClick={() => setSelectedTab('security')}
//             className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
//             style={{
//               borderColor: selectedTab === 'security' ? COLORS.primary : 'transparent',
//               color: selectedTab === 'security' ? COLORS.primary : COLORS.secondary,
//             }}
//             aria-label={t('security_status')}
//           >
//             <Shield size={16} className="inline mr-2" />
//             {t('security_status')}
//           </button>
//           <button
//             onClick={() => setSelectedTab('activity')}
//             className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 whitespace-nowrap`}
//             style={{
//               borderColor: selectedTab === 'activity' ? COLORS.primary : 'transparent',
//               color: selectedTab === 'activity' ? COLORS.primary : COLORS.secondary,
//             }}
//             aria-label={t('user_activity')}
//           >
//             <Activity size={16} className="inline mr-2" />
//             {t('user_activity')}
//           </button>
//         </div>
//       </div>

//       <div className="p-6">
//         {isLoading ? (
//           <div className="h-64 flex items-center justify-center">
//             <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: `${COLORS.secondary}50`, borderTopColor: COLORS.primary }}></div>
//           </div>
//         ) : (
//           <>
//             {selectedTab === 'overview' && (
//               <div>
//                 <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.darkText }}>{t('apps_performance_summary')}</h3>

//                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                   <StatSummaryCard
//                     title={t('authentication_rate')}
//                     value={averages.auth}
//                     icon={Users}
//                     color={COLORS.primary}
//                     trend={5.2}
//                     onClick={() => navigate('/users-management')}
//                   />
//                   <StatSummaryCard
//                     title={t('failed_logins')}
//                     value={averages.failed}
//                     icon={AlertTriangle}
//                     color={COLORS.secondary}
//                     trend={-3.1}
//                     onClick={() => navigate('/security-settings')}
//                   />
//                   <StatSummaryCard
//                     title={t('locked_accounts')}
//                     value={averages.locked}
//                     icon={LockIcon}
//                     color={CHART_COLORS.locked}
//                     trend={1.8}
//                     onClick={() => navigate('/security-settings')}
//                   />
//                   <StatSummaryCard
//                     title={t('inactive_users')}
//                     value={averages.inactive}
//                     icon={UserX}
//                     color={COLORS.darkText}
//                     trend={-2.4}
//                     onClick={() => navigate('/users-management')}
//                   />
//                 </div>

//                 <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30` }}>
//                   <div className="flex items-start">
//                     <Zap size={20} className="mr-3 mt-0.5 flex-shrink-0" style={{ color: COLORS.primary }} />
//                     <div>
//                       <h4 className="font-medium mb-1" style={{ color: COLORS.darkText }}>{t('quick_insights')}</h4>
//                       <p className="text-sm" style={{ color: COLORS.darkText }}>
//                         {averages.auth < 70
//                           ? t('low_authentication_insight')
//                           : averages.failed > 15
//                           ? t('high_failure_insight')
//                           : averages.locked > 10
//                           ? t('high_lockout_insight')
//                           : averages.inactive > 30
//                           ? t('high_inactivity_insight')
//                           : t('healthy_metrics_insight')}
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="flex justify-between items-center mt-8">
//                   <h3 className="text-lg font-semibold" style={{ color: COLORS.darkText }}>{t('recommended_actions')}</h3>
//                   <a href="#" className="text-sm font-medium hover:underline" style={{ color: COLORS.primary }}>
//                     {t('view_all_recommendations')}
//                   </a>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
//                   <button
//                     onClick={() => navigate('/users-management')}
//                     className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
//                     aria-label={t('manage_users')}
//                   >
//                     <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.primary}20`, color: COLORS.primary }}>
//                       <Users size={16} />
//                     </div>
//                     <div className="text-left">
//                       <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('manage_users')}</h4>
//                       <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('manage_users_description')}</p>
//                     </div>
//                   </button>

//                   <button
//                     onClick={() => navigate('/security-settings')}
//                     className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
//                     aria-label={t('security_settings')}
//                   >
//                     <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.secondary}20`, color: COLORS.secondary }}>
//                       <Shield size={16} />
//                     </div>
//                     <div className="text-left">
//                       <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('security_settings')}</h4>
//                       <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('security_settings_description')}</p>
//                     </div>
//                   </button>

//                   <button
//                     onClick={() => navigate('/notification-settings')}
//                     className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all flex items-start"
//                     aria-label={t('notifications')}
//                   >
//                     <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: `${COLORS.primary}10`, color: COLORS.primary }}>
//                       <Bell size={16} />
//                     </div>
//                     <div className="text-left">
//                       <h4 className="font-medium" style={{ color: COLORS.darkText }}>{t('notifications')}</h4>
//                       <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>{t('notifications_description')}</p>
//                     </div>
//                   </button>
//                 </div>
//               </div>
//             )}

//             {selectedTab === 'security' && (
//               <div>
//                 <h3 className="text-lg font-semibold mb-6" style={{ color: COLORS.darkText }}>{t('security_status_overview')}</h3>

//                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//                   <div className="bg-white p-4 rounded-lg border border-slate-200">
//                     <h4 className="font-medium mb-4" style={{ color: COLORS.darkText }}>{t('auth_status_distribution')}</h4>
//                     <div className="h-64">
//                       <ResponsiveContainer width="100%" height="100%">
//                         <PieChart>
//                           <Pie
//                             data={securityStatusData}
//                             cx="50%"
//                             cy="50%"
//                             innerRadius={60}
//                             outerRadius={80}
//                             paddingAngle={2}
//                             dataKey="value"
//                           >
//                             {securityStatusData.map((entry, index) => (
//                               <Cell key={`cell-${index}`} fill={entry.fill} />
//                             ))}
//                           </Pie>
//                           <Tooltip formatter={(value) => [`${value}%`]} />
//                           <Legend />
//                         </PieChart>
//                       </ResponsiveContainer>
//                     </div>
//                   </div>

//                   <div className="bg-white p-4 rounded-lg border border-slate-200">
//                     <h4 className="font-medium mb-2" style={{ color: COLORS.darkText }}>{t('security_risk_assessment')}</h4>

//                     <div className="space-y-4 mt-4">
//                       <div>
//                         <div className="flex justify-between mb-1">
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('authentication_health')}</span>
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.auth}%</span>
//                         </div>
//                         <div className="w-full bg-slate-200 rounded-full h-2">
//                           <div
//                             className="h-2 rounded-full"
//                             style={{
//                               width: `${averages.auth}%`,
//                               backgroundColor: averages.auth > 80 ? '#4caf50' : averages.auth > 60 ? CHART_COLORS.locked : COLORS.primary,
//                             }}
//                           ></div>
//                         </div>
//                       </div>

//                       <div>
//                         <div className="flex justify-between mb-1">
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('login_failures')}</span>
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.failed}%</span>
//                         </div>
//                         <div className="w-full bg-slate-200 rounded-full h-2">
//                           <div
//                             className="h-2 rounded-full"
//                             style={{
//                               width: `${averages.failed}%`,
//                               backgroundColor: averages.failed < 10 ? '#4caf50' : averages.failed < 20 ? CHART_COLORS.locked : COLORS.primary,
//                             }}
//                           ></div>
//                         </div>
//                       </div>

//                       <div>
//                         <div className="flex justify-between mb-1">
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('account_lockouts')}</span>
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{averages.locked}%</span>
//                         </div>
//                         <div className="w-full bg-slate-200 rounded-full h-2">
//                           <div
//                             className="h-2 rounded-full"
//                             style={{
//                               width: `${averages.locked}%`,
//                               backgroundColor: averages.locked < 5 ? '#4caf50' : averages.locked < 15 ? CHART_COLORS.locked : COLORS.primary,
//                             }}
//                           ></div>
//                         </div>
//                       </div>

//                       <div>
//                         <div className="flex justify-between mb-1">
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{t('user_activity')}</span>
//                           <span className="text-sm font-medium" style={{ color: COLORS.darkText }}>{100 - averages.inactive}%</span>
//                         </div>
//                         <div className="w-full bg-slate-200 rounded-full h-2">
//                           <div
//                             className="h-2 rounded-full"
//                             style={{
//                               width: `${100 - averages.inactive}%`,
//                               backgroundColor: averages.inactive < 20 ? '#4caf50' : averages.inactive < 40 ? CHART_COLORS.locked : COLORS.primary,
//                             }}
//                           ></div>
//                         </div>
//                       </div>
//                     </div>

//                     <button
//                       onClick={() => navigate('/security-settings')}
//                       className="mt-6 w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
//                       style={{ backgroundColor: COLORS.primary }}
//                       aria-label={t('review_security_settings')}
//                     >
//                       {t('review_security_settings')}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {selectedTab === 'activity' && (
//               <div>
//                 <h3 className="text-lg font-semibold mb-6" style={{ color: COLORS.darkText }}>{t('user_activity_metrics')}</h3>

//                 <div className="grid grid-cols-1 gap-6">
//                   <div className="bg-white p-4 rounded-lg border border-slate-200">
//                     <h4 className="font-medium mb-4" style={{ color: COLORS.darkText }}>{t('active_vs_inactive_users')}</h4>
//                     <div className="h-64">
//                       <ResponsiveContainer width="100%" height="100%">
//                         <BarChart
//                           data={appStats.map((app: { name: any; inactivePercentage: number }) => ({
//                             name: app.name,
//                             active: 100 - app.inactivePercentage,
//                             inactive: app.inactivePercentage,
//                           }))}
//                           margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
//                         >
//                           <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={`${COLORS.secondary}30`} />
//                           <XAxis
//                             dataKey="name"
//                             angle={-45}
//                             textAnchor="end"
//                             height={70}
//                             tick={{ fontSize: 11, fill: COLORS.darkText }}
//                           />
//                           <YAxis domain={[0, 100]} tick={{ fill: COLORS.darkText }} />
//                           <Tooltip />
//                           <Legend />
//                           <Bar dataKey="active" stackId="a" fill={CHART_COLORS.auth} name={t('active_users')} />
//                           <Bar dataKey="inactive" stackId="a" fill={CHART_COLORS.inactive} name={t('inactive_users')} />
//                         </BarChart>
//                       </ResponsiveContainer>
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     <div className="bg-white p-4 rounded-lg border border-slate-200">
//                       <h4 className="font-medium mb-3" style={{ color: COLORS.darkText }}>{t('user_engagement_summary')}</h4>
//                       <p className="text-sm mb-4" style={{ color: COLORS.secondary }}>
//                         {t('user_engagement_description',
//                          { active: Math.round(100 - averages.inactive),
//                           inactive: averages.inactive,
//                         })}
//                       </p>
//                       <button
//                         onClick={() => navigate('/users-management')}
//                         className="w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
//                         style={{ backgroundColor: COLORS.primary }}
//                         aria-label={t('manage_users')}
//                       >
//                         {t('manage_users')}
//                       </button>
//                     </div>

//                     <div className="bg-white p-4 rounded-lg border border-slate-200">
//                       <h4 className="font-medium mb-3" style={{ color: COLORS.darkText }}>{t('security_event_summary')}</h4>
//                       <p className="text-sm mb-4" style={{ color: COLORS.secondary }}>
//                         {t('security_event_description', {
//                           failed: averages.failed,
//                           locked: averages.locked,
//                         })}
//                       </p>
//                       <button
//                         onClick={() => navigate('/notification-settings')}
//                         className="w-full py-2 rounded-lg hover:bg-opacity-90 transition-colors text-white"
//                         style={{ backgroundColor: COLORS.primary }}
//                         aria-label={t('configure_alerts')}
//                       >
//                         {t('configure_alerts')}
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default function LandingPage() {
//   const navigate = useNavigate();
//   const { t } = useTranslation();
//   const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>({ label: t('3_months'), value: 3 });
//   const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
//   const [isMenuOpen, setIsMenuOpen] = useState(false);
//   const [isLoading, setIsLoading] = useState(true);
//   const companyId = localStorage.getItem('company');
//   const API_BASE_URL = import.meta.env.VITE_API;
//   const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

//   const PERIOD_OPTIONS: PeriodOption[] = [
//     { label: t('months'), value: 3 },
//     { label: t('monthss'), value: 6 },
//     { label: t('year'), value: 12 },
//     { label: t('all_time'), value: 0 },
//   ];

//   // Fetch applications data
//   const { data: applicationsData, error: applicationsError } = useSWR(
//     companyId ? `${API_BASE_URL}/Company/${companyId}/applications` : null,
//     axiosFetcher
//   );

//   // Application stats state
//   const [appStats, setAppStats] = useState<AppStats[]>([]);

//   // Fetch users data for each application and calculate stats
//   useEffect(() => {
//     const fetchUsersData = async () => {
//       if (!applicationsData?.data || !companyId) return;

//       setIsLoading(true);
//       const stats: AppStats[] = [];

//       for (const app of applicationsData.data) {
//         try {
//           const response = await axios.get(
//             `${API_BASE_URL}/Company/${companyId}/applications/${app.id}/users`,
//             {
//               withCredentials:true,
//               // headers: {
//               //   Authorization: `Bearer ${localStorage.getItem('access_token')}`,
//               // },
//             }
//           );

//           if (response.data && response.data.data) {
//             const users = response.data.data;
//             const totalUsers = users.length;

//             if (totalUsers === 0) {
//               stats.push({
//                 name: app.displayName,
//                 authPercentage: 0,
//                 failedPercentage: 0,
//                 lockedPercentage: 0,
//                 inactivePercentage: 0,
//               });
//               continue;
//             }

//             const now = new Date();
//             const periodStart = new Date();
//             if (selectedPeriod.value > 0) {
//               periodStart.setMonth(now.getMonth() - selectedPeriod.value);
//             }

//             // Authentication rate
//             const authenticatedUsers = users.filter((user: { lastSuccessful: string | number | Date }) => {
//               if (!user.lastSuccessful) return false;
//               const lastAuthDate = new Date(user.lastSuccessful);
//               return selectedPeriod.value === 0 || lastAuthDate >= periodStart;
//             }).length;

//             // Failed logins
//             const failedLogins = users.filter((user: { failureResponse: string | number | Date }) => {
//               if (!user.failureResponse || user.failureResponse === '1960-06-01T00:00:00') return false;
//               const failureDate = new Date(user.failureResponse);
//               return selectedPeriod.value === 0 || failureDate >= periodStart;
//             }).length;

//             // Locked accounts
//             const lockedAccounts = users.filter((user: { status: string }) => user.status === 'Locked').length;

//             // Inactive users
//             const inactiveUsers = users.filter((user: { lastActivity: string | number | Date; lastSuccessful: string | number | Date }) => {
//               const lastActivityDate = user.lastActivity
//                 ? new Date(user.lastActivity)
//                 : user.lastSuccessful
//                 ? new Date(user.lastSuccessful)
//                 : null;
//               return !lastActivityDate || (selectedPeriod.value > 0 && lastActivityDate < periodStart);
//             }).length;

//             stats.push({
//               name: app.displayName,
//               authPercentage: Math.round((authenticatedUsers / totalUsers) * 100),
//               failedPercentage: Math.round((failedLogins / totalUsers) * 100),
//               lockedPercentage: Math.round((lockedAccounts / totalUsers) * 100),
//               inactivePercentage: Math.round((inactiveUsers / totalUsers) * 100),
//             });
//           }
//         } catch (error) {
//           console.error(`Error fetching users for app ${app.id}:`, error);
//         }
//       }
//       setAppStats(stats);
//       setIsLoading(false);
//     };

//     fetchUsersData();
//   }, [applicationsData, companyId, selectedPeriod, API_BASE_URL]);

//   // Prepare chart data
//   const getAuthChartData = () => {
//     return appStats.map((app) => ({
//       name: app.name,
//       value: app.authPercentage,
//       fill: CHART_COLORS.auth,
//     }));
//   };

//   const getFailedChartData = () => {
//     return appStats.map((app) => ({
//       name: app.name,
//       value: app.failedPercentage,
//       fill: CHART_COLORS.failed,
//     }));
//   };

//   const getLockedChartData = () => {
//     return appStats.map((app) => ({
//       name: app.name,
//       value: app.lockedPercentage,
//       fill: CHART_COLORS.locked,
//     }));
//   };

//   const getInactiveChartData = () => {
//     return appStats.map((app) => ({
//       name: app.name,
//       value: app.inactivePercentage,
//       fill: CHART_COLORS.inactive,
//     }));
//   };

//   const exportCSV = () => {
//     const headers = `${t('application')},${t('authentication_rate')},${t('failed_logins')},${t('locked_accounts')},${t('inactive_users')}\n`;
//     const rows = appStats
//       .map((app) => `"${app.name}",${app.authPercentage}%,${app.failedPercentage}%,${app.lockedPercentage}%,${app.inactivePercentage}%`)
//       .join('\n');

//     const csvContent = headers + rows;
//     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//     saveAs(blob, `${t('company_stats_report')}-${new Date().toISOString().split('T')[0]}.csv`);
//   };

//   // Error and loading states
//   const hasError = applicationsError;
//   const isInitialLoading = !applicationsData && !applicationsError;

//   return (
//     <div className="min-h-screen" style={{ backgroundColor: COLORS.light }}>
//       <div>
//         <Navbar0 />
//         <Navbar />
//       </div>

//       {/* Main Content */}
//       <div className="pt-16">
//         {/* Period Selector and Menu */}
//         <div className="bg-white border-b shadow-sm" style={{ borderColor: COLORS.border }}>
//           <div className="max-w-7xl mx-auto px-8 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
//             <div className="relative mb-4 sm:mb-0">
//               <div className="flex items-center">
//                 <span className="mr-2 font-medium" style={{ color: COLORS.dark }}>{t('period')}</span>
//                 <button
//                   onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
//                   className="flex items-center space-x-2 px-4 py-2 bg-white border rounded-lg hover:bg-slate-50 transition-colors focus:outline-none"
//                   style={{ borderColor: COLORS.border, color: COLORS.dark }}
//                   aria-label={t('select_period')}
//                 >
//                   <span>{selectedPeriod.label}</span>
//                   <ChevronDown size={16} style={{ color: COLORS.secondary }} />
//                 </button>
//               </div>

//               {isPeriodDropdownOpen && (
//                 <div className="absolute left-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-10" style={{ borderColor: COLORS.border }}>
//                   {PERIOD_OPTIONS.map((option) => (
//                     <button
//                       key={option.value}
//                       onClick={() => {
//                         setSelectedPeriod(option);
//                         setIsPeriodDropdownOpen(false);
//                       }}
//                       className="block w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors first:rounded-t-lg last:rounded-b-lg border-b last:border-0"
//                       style={{ color: COLORS.dark, borderColor: COLORS.border }}
//                       aria-label={t('select_option', { label: option.label })}
//                     >
//                       {option.label}
//                     </button>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* Menu Button with Settings added */}
//             <div className="relative">
//               <button
//                 onClick={() => setIsMenuOpen(!isMenuOpen)}
//                 className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors focus:outline-none"
//                 style={{ backgroundColor: COLORS.primary }}
//                 aria-label={t('menu')}
//               >
//                 <Grid size={18} />
//                 <span className="font-medium">{t('menu')}</span>
//               </button>

//               {isMenuOpen && (
//                 <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10" style={{ borderColor: COLORS.border }}>
//                   <button
//                     onClick={() => navigate('/EntrepriseDashboard')}
//                     className="block w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b items-center"
//                     style={{ color: COLORS.dark, borderColor: COLORS.border }}
//                     aria-label={t('dashboard_company')}
//                   >
//                     <Grid size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('dashboard_company')}
//                   </button>
//                   {/* <button
//                     // onClick={() => ()}
//                     className="block w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b items-center"
//                     style={{ color: COLORS.dark, borderColor: COLORS.border }}
//                     aria-label={t('generate_new_key')}
//                   >
//                     <Key size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('generate_new_key')}
//                   </button> */}
//                   <button
//                     onClick={() => navigate('/BecomeClient')}
//                     className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b flex items-center"
//                     style={{ color: COLORS.dark, borderColor: COLORS.border }}
//                     aria-label={t('request_registration')}
//                   >
//                     <FileText size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('request_registration')}
//                   </button>
//                   <button
//                  onClick={() => {
//                  setIsMenuOpen(false); // Close the menu
//                setIsNotificationModalOpen(true); // Open notification modal
//   }}
//   className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b flex items-center"
//   style={{ color: COLORS.dark, borderColor: COLORS.border }}
//   aria-label={t('edit_notification_list')}
// >
//   <Bell size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('edit_notification_list')}
  
// </button>

//                   {/* <button
//                     onClick={() => navigate('/notification-list')}
//                     className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b flex items-center"
//                     style={{ color: COLORS.dark, borderColor: COLORS.border }}
//                     aria-label={t('edit_notification_list')}
//                   >
//                     <Bell size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('edit_notification_list')}
//                   </button> */}
//                   <button
//                     onClick={() => navigate('/settings')}
//                     className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center"
//                     style={{ color: COLORS.dark }}
//                     aria-label={t('settings')}
//                   >
//                     <Settings size={16} className="mr-2" style={{ color: COLORS.primary }} /> {t('settings')}
//                   </button>
                 
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Main Content Area */}
//         <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
//           {hasError ? (
//             <div className="p-4 rounded-md shadow-sm mb-6" style={{ backgroundColor: '#f8e6e7', borderLeft: `4px solid ${COLORS.primary}` }}>
//               <div className="flex">
//                 <AlertTriangle className="h-6 w-6 mr-3" style={{ color: COLORS.primary }} />
//                 <div>
//                   <p className="text-sm" style={{ color: COLORS.dark }}>{t('failed_to_load_data')}</p>
//                 </div>
//               </div>
//             </div>
//           ) : isInitialLoading ? (
//             <div className="flex justify-center items-center h-64">
//               <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: '#f0e6e7', borderTopColor: COLORS.primary }}></div>
//             </div>
//           ) : (
//             <>
//               {/* Stats Grid */}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
//                 <CompanyStatsChart
//                   title={t('authentication_rate_by_app')}
//                   icon={Users}
//                   data={getAuthChartData()}
//                   periodLabel={selectedPeriod.label}
//                   isLoading={isLoading}
//                   warning={appStats.some((app) => app.authPercentage < 50) ? t('low_authentication_warning') : undefined}
//                 />

//                 <CompanyStatsChart
//                   title={t('login_failures_by_app')}
//                   icon={AlertTriangle}
//                   data={getFailedChartData()}
//                   periodLabel={selectedPeriod.label}
//                   isLoading={isLoading}
//                   warning={appStats.some((app) => app.failedPercentage > 20) ? t('high_failure_warning') : undefined}
//                 />

//                 <CompanyStatsChart
//                   title={t('locked_accounts_by_app')}
//                   icon={LockIcon}
//                   data={getLockedChartData()}
//                   periodLabel={selectedPeriod.label}
//                   isLoading={isLoading}
//                   warning={appStats.some((app) => app.lockedPercentage > 10) ? t('high_lock_warning') : undefined}
//                 />

//                 <CompanyStatsChart
//                   title={t('user_inactivity_by_app')}
//                   icon={UserX}
//                   data={getInactiveChartData()}
//                   periodLabel={selectedPeriod.label}
//                   isLoading={isLoading}
//                   warning={appStats.some((app) => app.inactivePercentage > 40) ? t('high_inactivity_warning') : undefined}
//                 />
//               </div>

//               {/* Export Section */}
//               <div className="bg-white p-6 rounded-xl shadow-md border mb-8" style={{ borderColor: COLORS.border }}>
//                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
//                   <div>
//                     <h3 className="text-xl font-semibold" style={{ color: COLORS.dark }}>{t('global_export')}</h3>
//                     <p className="mt-1" style={{ color: COLORS.secondary }}>{t('global_export_description')}</p>
//                   </div>
//                   <button
//                     onClick={exportCSV}
//                     className="flex items-center px-5 py-2.5 text-white rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
//                     style={{ backgroundColor: COLORS.primary }}
//                     disabled={isLoading || appStats.length === 0}
//                     aria-label={t('export_full_report')}
//                   >
//                     <Download size={18} className="mr-2" />
//                     {t('export_full_report')}
//                   </button>
//                 </div>

//                 {isLoading ? (
//                   <div className="mt-4 flex items-center" style={{ color: COLORS.secondary }}>
//                     <div className="w-4 h-4 border-2 rounded-full animate-spin mr-2" style={{ borderColor: '#e9ecf0', borderTopColor: COLORS.primary }}></div>
//                     <span>{t('preparing_data')}</span>
//                   </div>
//                 ) : appStats.length === 0 ? (
//                   <div className="mt-2" style={{ color: COLORS.secondary }}>{t('no_data_for_export')}</div>
//                 ) : null}
//               </div>

//               {/* Analytics Hub */}
//               <AnalyticsHub appStats={appStats} isLoading={isLoading} navigate={navigate} />
//             </>
//           )}
//         </div>
//         <NotificationListModal 
//           isOpen={isNotificationModalOpen} 
//          onClose={() => setIsNotificationModalOpen(false)} 
//           />
//       </div>
      
//     </div>
//   );
// }





